import { createStyles, Group, Menu, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDotsVertical, IconDragDrop, IconPencil, IconRowInsertTop, IconTrashX } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useConfirm } from 'components/modals/message/MessageProvider';
import {
  IConfigureInputParams,
  useInputConfigurator,
} from 'pages/revisions-module/template-editor/editors/form/configurator/InputConfiguratorProvider';
import { IFormInput } from 'pages/revisions-module/template-editor/editors/form/types';
import ActionIcon from 'components/ActionIcon';
import FormInputPreview from 'pages/revisions-module/template-editor/editors/form/input/FormInputPreview';
import { useFormInputs } from 'pages/revisions-module/template-editor/editors/form/input/FormInputsDataProvider';
import { useFormRows } from 'pages/revisions-module/template-editor/editors/form/row/FormRowsDataProvider';
import { noop } from 'lodash';

/**
 * Parameters of the FormInput component.
 */
interface FormInputProps {
  index: number;
  sectionId: string;
  rowId: string;
  data: IFormInput;
  configureInputParams?: Partial<IConfigureInputParams>;
  disableMove?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  startMovingInputToRow?: (inputId: string) => void;
}

/**
 * Styles for the FormInput component.
 */
const useStyles = createStyles<'wrapper', { menuOpened: boolean }>((theme, { menuOpened = false }) => {
  const invisibleBorder = `${rem(1)} dashed transparent`;
  const activeBorder = `${rem(1)} dashed ${theme.colors.red[5]}`;

  return {
    wrapper: {
      border: menuOpened ? activeBorder : invisibleBorder,
      borderRadius: rem(8),
      transition: 'all 100ms ease',
      '& .actions': {
        opacity: menuOpened ? 1 : 0,
        transition: 'opacity 100ms ease',
      },
      ':hover': {
        border: activeBorder,
        '& .actions': {
          opacity: 1,
        },
      },
    },
  };
});

/**
 * A single input in a form row.
 */
export default function FormInput({
  index,
  sectionId,
  rowId,
  data,
  disableMove = false,
  configureInputParams = {},
  onDragStart = noop,
  onDragEnd = noop,
  startMovingInputToRow = noop,
}: FormInputProps) {
  const [menuOpened, { open: openMenu, close: closeMenu }] = useDisclosure(false);
  const styleParams = useMemo(() => ({ menuOpened }), [menuOpened]);
  const { classes } = useStyles(styleParams);
  const { confirm } = useConfirm();
  const { moveInputToNewRow } = useFormRows();
  const { removeInput, updateInputSpec } = useFormInputs();
  const { configureInput } = useInputConfigurator();

  const wrapperClassName = useMemo(() => `${classes.wrapper} form-input`, [classes.wrapper]);

  /** Confirm and remove the input. */
  const confirmRemoveInput = () =>
    confirm({
      title: 'Odstrániť pole',
      content: 'Naozaj chcete odstrániť toto pole? Táto akcia je nevratná.',
      onConfirm: () => removeInput(sectionId, rowId, data.id),
    });

  /** Open the input configurator. */
  const reconfigureInput = () =>
    configureInput({
      ...configureInputParams,
      primaryButtonLabel: 'Uložiť zmeny',
      context: data.context,
      initialValues: data.spec,
      readOnly: { name: true, type: true },
      onSubmit: (spec) => updateInputSpec(sectionId, rowId, data.id, spec),
    });

  return (
    <Group p={8} position="apart" noWrap align="end" className={wrapperClassName} pos="relative" data-index={index}>
      <FormInputPreview data={data} draggable={!disableMove} onDragStart={onDragStart} onDragEnd={onDragEnd} />
      <Menu opened={menuOpened} onOpen={openMenu} onClose={closeMenu} withinPortal>
        <Menu.Target>
          <ActionIcon pos="absolute" top={4} right={4} w={24} h={24} p={4} className="actions" variant="subtle-gray">
            <IconDotsVertical />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item icon={<IconPencil />} color="gray.7" onClick={reconfigureInput}>
            Upraviť pole
          </Menu.Item>
          {!disableMove && (
            <Menu.Item
              icon={<IconRowInsertTop />}
              color="gray.7"
              onClick={() => moveInputToNewRow(sectionId, rowId, data.id)}
            >
              Presunúť do nového riadku
            </Menu.Item>
          )}
          {!disableMove && (
            <Menu.Item icon={<IconDragDrop />} color="gray.7" onClick={() => startMovingInputToRow(data.id)}>
              Presunúť do existujúceho riadku
            </Menu.Item>
          )}
          <Menu.Item icon={<IconTrashX />} color="red.8" onClick={confirmRemoveInput} disabled={!data.deletable}>
            Odstrániť pole
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
